import React, { useEffect, useMemo, useState } from "react";
import {
  cancelReservation,
  getReservationByUserId,
  updateReservation,
} from "../reservation/ReservationServices";
import Modal from "../../utilities/components/Modal";
import KeycloakService from "../config/KeycloakService";
import ReservationForm from "../reservation/ReservationForm";
import Cancellation from "../../utilities/components/Cancellation";
import { ReservationModel } from "../../utilities/models/ReservationModel";
import { useLoadingSpinner } from "../../utilities/components/LoadingSpinnerProvider";
import { EntitiesListResponseModel } from "../../utilities/models/EntitiesListResponseModel";
import BookingCard from "./management/BookingCard";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";

/**
 * The component to manage the user's reservations.
 * @returns The component to manage the user's reservations.
 */
const BookingManagement = () => {
  const setIsLoading = useLoadingSpinner();
  const [state, setState] = useState(false);
  const [objectId, setObjectId] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [params, setParams] = useState({ size: 9, page: 0 });
  const [reservation, setReservation] = useState(ReservationModel);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [reservations, setReservations] = useState(EntitiesListResponseModel);
  const [recentlyChangedReservation, setRecentlyChangedReservation] = useState(null);

  const successMessage = useMemo(() => {
    if (!recentlyChangedReservation) {
      return "";
    }

    return recentlyChangedReservation.action === "created"
      ? "Your new reservation has been submitted and highlighted below."
      : "Your reservation update request was sent and highlighted below.";
  }, [recentlyChangedReservation]);

  const lunchEditionModal = (reservation) => {
    setReservation(reservation);
    setIsEditModalOpen(true);
  };

  const lunchCreationModal = () => {
    setIsCreateModalOpen(true);
  };

  const lunchCancellationModal = (id) => {
    setObjectId(id);
    setIsCancelModalOpen(true);
  };

  const cancelReservationHandler = async () => {
    setApiError(false);
    setIsLoading(true);

    try {
      await cancelReservation(objectId, setState, setIsLoading, setApiError);
      setIsCancelModalOpen(false);
    } catch {
      // Error feedback is handled in the service layer.
    }
  };

  useEffect(() => {
    setApiError(false);
    setIsLoading(true);

    getReservationByUserId(
      KeycloakService.getUserId(),
      params,
      setReservations,
      setApiError,
      setIsLoading,
    ).catch(() => {
      // Error feedback is handled in the service layer.
    });
  }, [state, setIsLoading, params]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < reservations.totalPages) {
      setParams((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleReservationSuccess = (savedReservation, action) => {
    if (!savedReservation) {
      return;
    }

    setRecentlyChangedReservation({
      id: savedReservation.id,
      startingDateTime: savedReservation.startingDateTime,
      action,
      timestamp: Date.now(),
    });
  };

  useEffect(() => {
    if (!recentlyChangedReservation?.id) {
      return;
    }

    const highlightedCard = document.querySelector(
      `[data-reservation-id="${recentlyChangedReservation.id}"]`,
    );

    highlightedCard?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  }, [reservations, recentlyChangedReservation]);

  useEffect(() => {
    if (!recentlyChangedReservation) {
      return undefined;
    }

    const timeoutId = globalThis.setTimeout(() => {
      setRecentlyChangedReservation(null);
    }, 5000);

    return () => globalThis.clearTimeout(timeoutId);
  }, [recentlyChangedReservation]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            My Reservations
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            Manage your event bookings and status.
          </p>
        </div>

        <button
          onClick={lunchCreationModal}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm"
        >
          <Plus size={20} />
          <span>New Reservation</span>
        </button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 shadow-sm dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
          {successMessage}
        </div>
      )}

      {/* Grid Content */}
      {reservations.content.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {reservations.content.map((res) => (
            <BookingCard
              key={res.id}
              reservation={res}
              onEdit={lunchEditionModal}
              onCancel={(id) => lunchCancellationModal(id)}
              isHighlighted={recentlyChangedReservation?.id === res.id}
              highlightLabel={
                recentlyChangedReservation?.id === res.id
                  ? recentlyChangedReservation.action
                  : null
              }
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 border-dashed">
          <p className="text-slate-500 dark:text-slate-400 text-lg">
            {apiError
              ? "We couldn’t load your reservations right now. Please try again shortly."
              : "You haven't made any reservations yet."}
          </p>
          <button
            onClick={lunchCreationModal}
            className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
          >
            Book your first event &rarr;
          </button>
        </div>
      )}

      {/* Pagination */}
      {reservations.totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-8">
          <button
            onClick={() => handlePageChange(params.page - 1)}
            disabled={params.page === 0}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
            Page {params.page + 1} of {reservations.totalPages}
          </span>
          <button
            onClick={() => handlePageChange(params.page + 1)}
            disabled={params.page >= reservations.totalPages - 1}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}

      {/* Modals */}
      <Modal isOpen={isEditModalOpen} onClose={setIsEditModalOpen}>
        <ReservationForm
          reservation={reservation}
          updateReservation={updateReservation}
          isUpdating={true}
          toggleModal={setIsEditModalOpen}
          setState={setState}
          onSuccess={handleReservationSuccess}
        />
      </Modal>
      <Modal isOpen={isCreateModalOpen} onClose={setIsCreateModalOpen}>
        <ReservationForm
          toggleModal={setIsCreateModalOpen}
          setState={setState}
          onSuccess={handleReservationSuccess}
        />
      </Modal>
      <Modal isOpen={isCancelModalOpen} onClose={setIsCancelModalOpen}>
        <Cancellation
          actionHandler={cancelReservationHandler}
          setOpen={setIsCancelModalOpen}
          title="Cancel Reservation"
          warningMessage="Are you sure you want to cancel this reservation?"
          actionText="Cancel Reservation"
          cancelText="Go Back"
        />
      </Modal>
    </div>
  );
};

export default BookingManagement;
